import { parseISO, isValid } from 'date-fns';

// Date-only formats (no time component)
// Note: Using `as const` for type inference. The `satisfies` keyword is not used
// because the current Babel version (7.12.16) doesn't support it (requires 7.20+).
// Type safety is maintained through the DateOnlyFormat type exported below.
const dateOnlyFormatMap = {
  // Basic date formats
  ddMMMyyyy: { day: 'numeric', month: 'short', year: 'numeric' }, // 28 Aug 2025
  ddMMMyy: { day: 'numeric', month: 'short', year: '2-digit' }, // 28 Aug 25
  ddMMM: { day: 'numeric', month: 'short' }, // 28 Aug
  MMMdd: { month: 'short', day: 'numeric' }, // Aug 28
  'MMM dd': { month: 'short', day: '2-digit' }, // Aug 28 (with leading zero)
  MMMddyyyy: { month: 'short', day: 'numeric', year: 'numeric' }, // Aug 28 2025
  MMMyyyy: { month: 'short', year: 'numeric' }, // Aug 2025
  MMMyy: { month: 'short', year: '2-digit' }, // Aug 25
  MMMM: { month: 'long' }, // August
  MMM: { month: 'short' }, // Aug
  dd: { day: '2-digit' }, // 28
  yyyy: { year: 'numeric' }, // 2025

  // Standard date formats
  'MMMM d, yyyy': { month: 'long', day: 'numeric', year: 'numeric' }, // August 5, 2025
  'MMMM dd, yyyy': { month: 'long', day: '2-digit', year: 'numeric' }, // August 28, 2025
  'MMM d, yyyy': { month: 'short', day: 'numeric', year: 'numeric' }, // Aug 5, 2025
  'MMM dd, yyyy': { month: 'short', day: '2-digit', year: 'numeric' }, // Aug 28, 2025
  'MM/dd/yyyy': { month: '2-digit', day: '2-digit', year: 'numeric' }, // 12/25/2025
  'MMMM yyyy': { month: 'long', year: 'numeric' }, // August 2025
  'dd MMMM yyyy': { day: 'numeric', month: 'long', year: 'numeric' }, // 28 August 2025

  // Weekday with date formats
  'weekday, dd MMMM yyyy': { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }, // Thursday, 28 August 2025
  'weekday\ndd MMMM yyyy': { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }, // Thursday\n28 August 2025
  'weekday, MMMM d, yyyy': { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' }, // Tuesday, August 28, 2025
  'weekday, MMM d': { weekday: 'long', month: 'short', day: 'numeric' }, // Thursday, Jan 1
  'weekday-short, MMMM dd': { weekday: 'short', month: 'long', day: '2-digit' }, // Tue, August 28
  'weekday-short, MMM dd': { weekday: 'short', month: 'short', day: '2-digit' }, // Thu, Aug 08
  'weekday-short, MMM d': { weekday: 'short', month: 'short', day: 'numeric' }, // Thu, Jan 1
  'weekday-short, MMM d, yyyy': { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }, // Thu, Jan 1, 2026
  'E, MMM dd, yyyy': { weekday: 'short', month: 'short', day: '2-digit', year: 'numeric' }, // Thu, Aug 28, 2025

  // Ordinal date formats (English only)
  'MMMM do, yyyy': { month: 'long', day: 'numeric', year: 'numeric' }, // November 13th, 2025
} as const;

// Time-only formats (no date component)
// Note: Using `as const` for type inference (see dateOnlyFormatMap comment above)
const timeOnlyFormatMap = {
  'hh:mm': { hour: '2-digit', minute: '2-digit' }, // 04:00 PM or 16:00 based on the locale
  'h:mm': { hour: 'numeric', minute: '2-digit' }, // 4:00 PM or 16:00 based on the locale
} as const;

// DateTime formats (both date and time)
// Note: Using `as const` for type inference (see dateOnlyFormatMap comment above)
const dateTimeFormatMap = {
  'yyyy-MM-dd': { year: 'numeric', month: '2-digit', day: '2-digit' }, // 2025-08-28
  'yyyy-MM-dd HH:mm': { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }, // 2025-08-28 16:00
  'weekday-short, MMM do, h:mm': { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }, // Tue, Oct 4, 4:00
  'MMMM do, yyyy h:mm': { month: 'long', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }, // November 13th, 2025 4:00pm
} as const;

// Consolidated format map for internal use
const formatMap = {
  ...dateOnlyFormatMap,
  ...timeOnlyFormatMap,
  ...dateTimeFormatMap,
} as const;

export type NullableDateInput = string | Date | number | null | undefined;
export type DateOnlyFormat = keyof typeof dateOnlyFormatMap;
export type TimeOnlyFormat = keyof typeof timeOnlyFormatMap;
export type DateTimeFormat = keyof typeof dateTimeFormatMap;
export type DateFormat = keyof typeof formatMap;
export type FormatDateOptions = Readonly<{
  timezone?: string | null;
  titlecase?: boolean;
  /** Forces 12-hour (`true`) or 24-hour (`false`) rendering of the time parts; omit to let the
   *  locale decide. Ignored by formats with no time component. */
  hour12?: boolean;
  /** ISO-8601 timestamp with embedded UTC offset (e.g. "2027-02-28T16:00:00.000-08:00").
   *  When provided alongside `timezone`, the offset from this string is used to build a
   *  fixed-offset timezone for `Intl.DateTimeFormat`, avoiding browser DST resolution bugs
   *  (notably iOS Safari applying the *current* DST offset instead of the target date's). */
  isoTimestamp?: string | null;
}>;
export type DateRangeFormat = Extract<DateOnlyFormat, 'MM/dd/yyyy' | 'MMMM do, yyyy' | 'weekday, dd MMMM yyyy'>;

// Map legacy date-fns format strings to new formatMap keys for backward compatibility
// Old ecards may have these formats stored in displayDateFormat
const LEGACY_FORMAT_MAP: Record<string, DateFormat> = {
  'EEEE, dd MMMM yyyy': 'weekday, dd MMMM yyyy',
  'EEEE, MMMM dd, yyyy': 'E, MMM dd, yyyy',
} as const;

/**
 * Normalizes legacy date-fns format strings to supported formatMap keys.
 * Preserves backward compatibility with old ecards that may have legacy formats stored.
 */
export const normalizeDateFormat = (format: string): DateFormat => {
  return (LEGACY_FORMAT_MAP[format] || format) as DateFormat;
};

// Languages that use English-style ordinal suffixes (st, nd, rd, th) in dates
// Matches all English variants (en, en-US, en-GB, etc.) case-insensitively
const ENGLISH_LANGUAGE_REGEX = /^en(-.*)?$/i;

// Add ordinal suffix to day numbers (1st, 2nd, 3rd, 4th, etc.)
// Logic: Use modulo 10 to get the last digit (1→st, 2→nd, 3→rd), but exclude
// the special cases of 11th, 12th, and 13th which use 'th' instead of following
// the standard pattern. Modulo 100 helps identify these teen numbers.
// NOTE: Only applies to English and variants; other languages don't use ordinal suffixes in dates
const addOrdinalSuffix = (day: number, language: string): string => {
  // Only apply ordinal suffixes for languages that use them
  if (!ENGLISH_LANGUAGE_REGEX.test(language)) {
    return day.toString();
  }

  const j = day % 10;
  const k = day % 100;
  if (j === 1 && k !== 11) {
    return day + 'st';
  }
  if (j === 2 && k !== 12) {
    return day + 'nd';
  }
  if (j === 3 && k !== 13) {
    return day + 'rd';
  }
  return day + 'th';
};

/**
 * Extracts the UTC offset from an ISO-8601 timestamp and returns a fixed-offset
 * timezone identifier that `Intl.DateTimeFormat` understands (e.g. "Etc/GMT+8").
 *
 * This avoids relying on the browser's IANA tz database for DST resolution — iOS Safari
 * is known to incorrectly apply the *current* DST offset instead of the target date's
 * offset for certain IANA zones (e.g. America/Vancouver showing PDT in winter).
 *
 * Falls back to the original IANA timezone when:
 * - No ISO timestamp is provided
 * - The offset cannot be parsed from the timestamp
 * - The offset has non-zero minutes (e.g. +05:30 for Asia/Kolkata) since Etc/GMT±N
 *   only supports whole-hour offsets
 *
 * @param ianaTimezone - The original IANA timezone (e.g. "America/Vancouver")
 * @param isoTimestamp - ISO-8601 string with offset (e.g. "2027-02-28T16:00:00.000-08:00")
 * @returns A fixed-offset timezone string (e.g. "Etc/GMT+8") or the original IANA timezone
 */
export const resolveFixedOffsetTimezone = (ianaTimezone: string, isoTimestamp?: string | null): string => {
  if (!isoTimestamp) return ianaTimezone;

  // Match the UTC offset at the end of an ISO-8601 string: +HH:MM or -HH:MM
  const match = isoTimestamp.match(/([+-])(\d{2}):(\d{2})$/);
  if (!match) return ianaTimezone;

  const hours = parseInt(match[2], 10);
  const minutes = parseInt(match[3], 10);

  // Etc/GMT only supports whole-hour offsets — fall back for half-hour zones (IST, NPT, etc.)
  if (minutes !== 0) return ianaTimezone;

  // UTC+0 edge case
  if (hours === 0) return 'UTC';

  // Etc/GMT signs are inverted: Etc/GMT+8 = UTC-8, Etc/GMT-5 = UTC+5
  const etcSign = match[1] === '+' ? '-' : '+';
  return `Etc/GMT${etcSign}${hours}`;
};

// Format using Intl; optionally title-case weekday/month while preserving literals/order
const formatDate = (language: string, date: Date, options: Intl.DateTimeFormatOptions, titlecase: boolean): string => {
  if (!titlecase) {
    return new Intl.DateTimeFormat(language, options).format(date);
  }

  // NOTE: In French, Spanish and Italian, days/months are normally lowercase (e.g. "jeudi 4 juin 2026")
  // However, when displaying the date in a heading, we capitalize the weekday and month (e.g. "Jeudi 4 Juin 2026")
  // to improve visual hierarchy and readability.
  // This is not standard grammar but matches user expectations of dates in UI headings
  const parts = new Intl.DateTimeFormat(language, options).formatToParts(date);
  const capitalize = (value: string) => value.slice(0, 1).toLocaleUpperCase(language) + value.slice(1);

  return parts.map(({ type, value }) => (type === 'weekday' || type === 'month' ? capitalize(value) : value)).join('');
};

const formatMultilineDate = (language: string, date: Date, format: Intl.DateTimeFormatOptions, titlecase: boolean) => {
  const { timeZone, weekday, ...dateParts } = format;

  const weekdayOptions = { weekday: weekday ?? 'long', timeZone };
  const dateOptions = { ...dateParts, timeZone };

  const weekdayPart = formatDate(language, date, weekdayOptions, titlecase);
  const datePart = formatDate(language, date, dateOptions, titlecase);

  return `${weekdayPart}\n${datePart}`;
};

export const formatDateForLanguage = (language: string, input: NullableDateInput, format: DateFormat, options: FormatDateOptions = {}): string | undefined => {
  if (!input) return undefined;

  const date = input instanceof Date ? input : typeof input === 'number' ? new Date(input) : parseISO(input);
  if (!isValid(date)) return undefined;

  // Normalize legacy formats for backward compatibility
  const normalizedFormat = normalizeDateFormat(format);

  // If format is still not in formatMap after normalization, return undefined
  if (!formatMap[normalizedFormat]) {
    return undefined;
  }

  const { timezone, isoTimestamp, titlecase = false, hour12 } = options;

  // When an ISO timestamp with an embedded offset is available, resolve to a fixed-offset
  // timezone to sidestep browser DST resolution bugs (iOS Safari, older WebKit).
  const effectiveTimezone = timezone ? resolveFixedOffsetTimezone(timezone, isoTimestamp) : undefined;

  const formatOptions = {
    ...formatMap[normalizedFormat],
    ...(effectiveTimezone && { timeZone: effectiveTimezone }),
    ...(hour12 !== undefined && { hour12 }),
  };

  if (normalizedFormat === 'weekday\ndd MMMM yyyy') {
    return formatMultilineDate(language, date, formatOptions, titlecase);
  }

  // Special handling for ordinal date formats
  const ordinalFormats: DateFormat[] = ['weekday-short, MMM do, h:mm', 'MMMM do, yyyy', 'MMMM do, yyyy h:mm'];
  if (ordinalFormats.includes(normalizedFormat)) {
    const parts = new Intl.DateTimeFormat(language, formatOptions).formatToParts(date);
    // Get the day from the formatted parts to respect timezone, fallback to date.getDate() if not found
    const dayPart = parts.find(part => part.type === 'day');
    const day = dayPart ? parseInt(dayPart.value, 10) : date.getDate();
    const dayWithOrdinal = addOrdinalSuffix(day, language);

    return parts
      .map(({ type, value }) => {
        if (type === 'day') {
          return dayWithOrdinal;
        }
        return value;
      })
      .join('');
  }

  // Preserve locale literals; optionally title-case only weekday/month parts
  return formatDate(language, date, formatOptions, titlecase);
};

// Range separator, matching the existing useEventLocaleFormatter().formatDateRange. Exported so surfaces that
// lay a formatted range out across lines can find the break point without re-guessing the literal.
export const RANGE_SEPARATOR = ' - ';

const parseDateInput = (input: NullableDateInput): Date | undefined => {
  if (!input) return undefined;
  const date = input instanceof Date ? input : typeof input === 'number' ? new Date(input) : parseISO(input);
  return isValid(date) ? date : undefined;
};

// Resolve the numeric year/month/day a date renders as in the display timezone, so the
// end-before-start guard agrees with the rendered output across timezone boundaries.
const getDisplayParts = (date: Date, timezone?: string | null): { year: number; month: number; day: number } => {
  const parts = new Intl.DateTimeFormat('en-US', { year: 'numeric', month: 'numeric', day: 'numeric', ...(timezone && { timeZone: timezone }) }).formatToParts(date);
  const get = (type: Intl.DateTimeFormatPartTypes) => Number(parts.find(part => part.type === type)?.value);
  return { year: get('year'), month: get('month'), day: get('day') };
};

// Sortable yyyymmdd key for comparing two display-timezone dates
const toDayKey = ({ year, month, day }: { year: number; month: number; day: number }): number => year * 10000 + month * 100 + day;

/**
 * Renders a date range in one of the three EventDetails format tokens, locale-aware.
 *
 * Collapsing is delegated to `Intl…formatRangeToParts`, so shared month/year fields collapse per
 * the locale's own word order (e.g. "June 12th - 14th, 2026" vs "12 - 14 de junio de 2026"). On top
 * of the native parts we add English ordinal suffixes (English-only, via addOrdinalSuffix), optional
 * heading titlecase for fr/es/it, and our `-` separator. Numeric never collapses (rendered in full).
 *
 * Edge cases: missing `end` → the single start date; missing/invalid `start` or `end` before `start` → undefined.
 */
export const formatDateRangeForLanguage = (
  language: string,
  start: NullableDateInput,
  end: NullableDateInput,
  format: DateRangeFormat,
  options: FormatDateOptions = {},
): string | undefined => {
  const { timezone, titlecase = false } = options;

  const startDate = parseDateInput(start);
  if (!startDate) return undefined;

  // No end date → render the single start date in the requested format
  if (!end) return formatDateForLanguage(language, start, format, options);

  const endDate = parseDateInput(end);
  if (!endDate) return undefined;

  // Reject an end that lands before start in the display timezone
  if (toDayKey(getDisplayParts(endDate, timezone)) < toDayKey(getDisplayParts(startDate, timezone))) return undefined;

  // Numeric never collapses — format each endpoint fully and join
  if (format === 'MM/dd/yyyy') {
    return `${formatDateForLanguage(language, start, format, options)}${RANGE_SEPARATOR}${formatDateForLanguage(language, end, format, options)}`;
  }

  // Let Intl collapse shared month/year per the locale's own word order, then post-process the parts.
  const intlOptions = { ...dateOnlyFormatMap[format], ...(timezone && { timeZone: timezone }) };
  const parts = new Intl.DateTimeFormat(language, intlOptions).formatRangeToParts(startDate, endDate);

  const ordinal = format === 'MMMM do, yyyy';
  const capitalize = (value: string) => value.slice(0, 1).toLocaleUpperCase(language) + value.slice(1);
  // The range separator is the lone literal between the start-range and end-range parts; other
  // shared literals (", 2026", " de ") sit outside this window, so normalize only that one.
  const lastStartIndex = parts.reduce((last, part, index) => (part.source === 'startRange' ? index : last), -1);
  const firstEndIndex = parts.findIndex(part => part.source === 'endRange');

  return parts
    .map((part, index) => {
      // Swap the dash itself and the whitespace hugging it, not the whole literal: ICU can fuse the separator
      // with punctuation that belongs to the start date (ja-JP emits `")～"`, closing the weekday parenthesis),
      // and replacing wholesale drops that character and leaves the parenthesis unbalanced. A locale whose
      // separator isn't dash-like keeps its own literal rather than being force-fitted to ours.
      if (part.type === 'literal' && index > lastStartIndex && index < firstEndIndex) return part.value.replace(/\s*[-–—~～]\s*/, RANGE_SEPARATOR);
      if (part.type === 'day' && ordinal) return addOrdinalSuffix(parseInt(part.value, 10), language);
      if (titlecase && (part.type === 'weekday' || part.type === 'month')) return capitalize(part.value);
      return part.value;
    })
    .join('');
};

export const getDateFormatterForLanguage = (language: string) => {
  return (input: NullableDateInput, format: DateFormat, options?: FormatDateOptions) => formatDateForLanguage(language, input, format, options);
};
